// Cómo se dicen las cosas en la tienda: presentaciones, cantidades, teléfonos y fechas, en palabras de todos los días.
import { DIA_SEMANA_ETIQUETA, diaSemana, hoyISO, sumarDias, type UnidadBulto, ZONA_HORARIA } from '../compartido';

const UNIDAD: Record<UnidadBulto, [string, string]> = {
  caja: ['caja', 'cajas'],
  fardo: ['fardo', 'fardos'],
  pack: ['pack', 'packs'],
  display: ['display', 'displays'],
  bolsa: ['bolsa', 'bolsas'],
  unidad: ['unidad', 'unidades'],
};

const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;

/** "3 cajas", "1 fardo". Sin unidad conocida: "3 bultos". */
export function cantidadEnUnidad(n: number, unidad: UnidadBulto | null | undefined): string {
  const u = unidad ? UNIDAD[unidad] : undefined;
  return u ? plural(n, u[0], u[1]) : plural(n, 'bulto', 'bultos');
}

export const nombreUnidad = (unidad: UnidadBulto | null | undefined): string => (unidad ? UNIDAD[unidad][0] : 'bulto');

/** "Caja x 20", "Pack x 6", "Por unidad". */
export function presentacion(unidad: UnidadBulto | null | undefined, porBulto: number | null | undefined): string {
  if (!unidad) return '';
  if (unidad === 'unidad' || !porBulto || porBulto <= 1)
    return unidad === 'unidad' ? 'Por unidad' : capitalizar(unidad);
  return `${capitalizar(UNIDAD[unidad][0])} x ${porBulto}`;
}

/** Lo que se va escribiendo, ya separado como se dice: "0981 123 456". */
export function telefonoEscrito(entrada: string): string {
  let d = entrada.replace(/\D/g, '');
  if (d.startsWith('595')) d = `0${d.slice(3)}`;
  d = d.slice(0, 10);
  if (d.length <= 4) return d;
  if (d.length <= 7) return `${d.slice(0, 4)} ${d.slice(4)}`;
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
}

/** "+595981123456" → "0981 123 456". */
export function telefonoVisible(e164: string | null | undefined): string {
  if (!e164) return '';
  const d = e164.replace(/\D/g, '');
  return telefonoEscrito(d.startsWith('595') ? `0${d.slice(3)}` : d);
}

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** "2026-10-16" → "jueves 16 de octubre". */
export function fechaLarga(fechaISO: string): string {
  const [, m, d] = fechaISO.split('-');
  return `${DIA_SEMANA_ETIQUETA[diaSemana(fechaISO)].toLowerCase()} ${Number(d)} de ${MESES[Number(m) - 1]}`;
}

/** "mañana, jueves 16 de octubre"; "hoy" y "pasado mañana" también; si no, la fecha larga. */
export function fechaEntrega(fechaISO: string, zona = ZONA_HORARIA, ahora = new Date()): string {
  const hoy = hoyISO(zona, ahora);
  const larga = fechaLarga(fechaISO);
  if (fechaISO === hoy) return `hoy, ${larga}`;
  if (fechaISO === sumarDias(hoy, 1)) return `mañana, ${larga}`;
  if (fechaISO === sumarDias(hoy, 2)) return `pasado mañana, ${larga}`;
  return larga;
}

/** "16/10" para listas. */
export const fechaCorta = (fechaISO: string): string => {
  const [, m, d] = fechaISO.split('-');
  return `${d}/${m}`;
};

/** Primer nombre para el saludo: "Rubén Gómez" → "Rubén". */
export const primerNombre = (s: string | null | undefined): string | null => s?.trim().split(/\s+/)[0] || null;

/** Dos letras para el cuadrito del producto: "Vaso térmico 180 ml" → "VT". Salta números y medidas. */
export function iniciales(nombre: string): string {
  const palabras = nombre
    .split(/[\s/-]+/)
    .filter((p) => /^[a-záéíóúñü]/i.test(p) && !/^(x|de|del|la|el|con|para|y|en)$/i.test(p));
  const letras = palabras.slice(0, 2).map((p) => p[0]!.toUpperCase());
  return letras.join('') || nombre.slice(0, 2).toUpperCase();
}
